import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useLocale, useTranslations } from "next-intl";
import { PConfirmDialog } from "@/components/PConfirmDialog/PConfirmDialog";
import { intlLocaleOf } from "@/lib/i18n/intlLocale";

/**
 * B2 (plan Rev. 5.2, FE-2/FE-3): the questions a field-scope edit asks before
 * it lands.
 *
 *  - `sensitive` — SENSITIVE / ART9 / unknown-level fields and the access data
 *    (`code`, `username`, `loginToken`) about to become visible in "All
 *    except": every path into Visible asks (Show, "Show all new", a view
 *    import's Add, the "Aktion" offer, a prerequisite grant).
 *  - `showAllNew` — "Show all new (normal fields)": ONE question naming the
 *    fields it releases.
 *  - `replace` — a view import's Replace on an "All except" role: it switches
 *    the role to "Only these".
 *
 * `ask` resolves `true` on confirm, `false` on cancel (or when the host
 * unmounts with the question open). `elevated` raises the dialog above a host
 * modal (the preview bar's editor).
 */
export type FieldScopeQuestion = "sensitive" | "showAllNew" | "replace";

type Pending = { kind: FieldScopeQuestion; names: string[]; resolve: (ok: boolean) => void };

export function useFieldScopeConfirm({ elevated = false }: { elevated?: boolean } = {}): {
  ask: (kind: FieldScopeQuestion, names: string[]) => Promise<boolean>;
  dialog: ReactNode;
} {
  const t = useTranslations("roleTemplates");
  const locale = useLocale();
  const [pending, setPending] = useState<Pending | null>(null);
  const pendingRef = useRef<Pending | null>(null);

  // A question left open by an unmounting host answers "no" — never a dangling promise.
  useEffect(() => () => pendingRef.current?.resolve(false), []);

  const ask = useCallback(
    (kind: FieldScopeQuestion, names: string[]) =>
      new Promise<boolean>((resolve) => {
        pendingRef.current?.resolve(false);
        const next: Pending = { kind, names, resolve };
        pendingRef.current = next;
        setPending(next);
      }),
    [],
  );

  const answer = (ok: boolean) => {
    const p = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    p?.resolve(ok);
  };

  let dialog: ReactNode = null;
  if (pending) {
    const names = new Intl.ListFormat(intlLocaleOf(locale), { style: "long", type: "conjunction" }).format(
      pending.names,
    );
    const count = pending.names.length;
    const copy =
      pending.kind === "sensitive"
        ? {
            title: t("fieldScope.sensitiveTitle"),
            message: t("fieldScope.sensitiveMessage", { count, names }),
            confirm: t("fieldScope.sensitiveConfirm"),
          }
        : pending.kind === "showAllNew"
          ? {
              title: t("fieldScope.showAllNewTitle"),
              message: t("fieldScope.showAllNewMessage", { count, names }),
              confirm: t("fieldScope.showAllNewConfirm"),
            }
          : {
              title: t("fieldScope.replaceTitle"),
              message: t("fieldScope.replaceMessage"),
              confirm: t("replace"),
            };
    dialog = (
      <PConfirmDialog
        title={copy.title}
        message={copy.message}
        confirmText={copy.confirm}
        elevated={elevated}
        onConfirm={() => answer(true)}
        onCancel={() => answer(false)}
      >
        <span hidden data-testid="field-scope-confirm" data-kind={pending.kind} />
      </PConfirmDialog>
    );
  }
  return { ask, dialog };
}
